import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  NavLink,
  NavLinkProps,
  useFetcher,
  useSubmit,
} from "@remix-run/react";
import {
  Avatar,
  Box,
  Button,
  Container,
  DropdownMenu,
  Flex,
  Heading,
  Text,
  // Popover,
  Theme,
} from "@radix-ui/themes";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";
import { useToast } from "./toast";
import logo from "~/images/logo.png";

export type NavItemProp = NavLinkProps &
  React.RefAttributes<HTMLAnchorElement> & {
    to: string;
    icon?: React.ReactNode;
    label: string;
  };
function NavItem(props: NavItemProp) {
  const { to, icon, label, ...rest } = props;
  return (
    <NavLink
      to={to}
      {...rest}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-lg text-[14px] transition-colors ${
          isActive
            ? "bg-[var(--accent-4)] text-[var(--accent-11)] font-medium"
            : "text-[var(--gray-11)] hover:bg-[var(--gray-3)]"
        }`
      }
    >
      {icon && <Box>{icon}</Box>}
      {label}
    </NavLink>
  );
}

export function Nav({
  navItems,
  bottomPopoverItems,
}: {
  navItems: NavItemProp[];
  bottomPopoverItems: NavItemProp[];
}) {
  return (
    <nav
      style={{
        width: "248px",
        flexShrink: 0,
        height: "100%",
        overflowY: "auto",
        borderRight: "1px solid var(--gray-a4)",
        background: "var(--gray-1)",
      }}
    >
      <Flex direction="column" gap="1" p="3">
        {navItems.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
      </Flex>

      {bottomPopoverItems.length > 0 && (
        <Flex direction="column" gap="1" px="3" pb="3">
          {bottomPopoverItems.map((item) => (
            <NavItem
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={item.label}
            />
          ))}
        </Flex>
      )}
    </nav>
  );
}

function SendPasswordResetEmailDialog({
  sendPasswordResetEmailActionPath,
  open,
  onOpenChange,
}: {
  sendPasswordResetEmailActionPath: string;
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
}) {
  const fetcher = useFetcher<{ success: boolean }>();
  const { toast } = useToast();

  const handleSubmitSendPasswordResetEmail = (event: React.FormEvent) => {
    event.preventDefault();
    fetcher.submit(
      {},
      {
        method: "post",
        action: sendPasswordResetEmailActionPath,
      }
    );
  };

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data?.success) {
      onOpenChange(false);
      toast({
        title: "再設定メールを送信しました",
        description: "メールのリンクからパスワードを再設定してください。",
        variant: "success",
      });
    }
  }, [fetcher.state]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <Theme>
          <Flex direction="column" gap="4">
            <DialogHeader>
              <DialogTitle>パスワード再設定</DialogTitle>
              <DialogDescription>
                ログインパスワードの再設定リンクをメールで送信します。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="justify-end">
              <fetcher.Form
                action={sendPasswordResetEmailActionPath}
                method="post"
                // className="text-left"
                onSubmit={handleSubmitSendPasswordResetEmail}
              >
                <Button type="submit" size="2">
                  送信する
                </Button>
              </fetcher.Form>
            </DialogFooter>
          </Flex>
        </Theme>
      </DialogContent>
    </Dialog>
  );
}

function AccountMenu({
  email,
  sendPasswordResetEmailActionPath,
  logoutActionPath,
}: {
  email: string;
  sendPasswordResetEmailActionPath: string;
  logoutActionPath: string;
}) {
  const submit = useSubmit();
  const [resetOpen, setResetOpen] = useState(false);
  const initial = email?.trim()?.charAt(0)?.toUpperCase() || "?";

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <button
            type="button"
            className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 hover:bg-gray-100"
          >
            <Avatar
              size="1"
              radius="full"
              color="teal"
              fallback={initial}
            />
            <Text size="2">{email}</Text>
            <ChevronDownIcon />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end">
          <DropdownMenu.Label>{email}</DropdownMenu.Label>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            onSelect={(event) => {
              event.preventDefault();
              setResetOpen(true);
            }}
          >
            パスワード再設定
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            color="red"
            onSelect={() =>
              submit(null, { method: "post", action: logoutActionPath })
            }
          >
            ログアウト
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <SendPasswordResetEmailDialog
        sendPasswordResetEmailActionPath={sendPasswordResetEmailActionPath}
        open={resetOpen}
        onOpenChange={setResetOpen}
      />
    </>
  );
}

export function TwoColumnLayout({
  headingText,
  headingDescription,
  navItems,
  bottomPopoverItems,
  children,
  email,
  sendPasswordResetEmailActionPath,
  logoutActionPath,
}: {
  headingText: string;
  headingDescription?: string;
  navItems: NavItemProp[];
  bottomPopoverItems: NavItemProp[];
  children: React.ReactNode;
  email: string;
  sendPasswordResetEmailActionPath: string;
  logoutActionPath: string;
}) {
  return (
    <Flex direction="column" style={{ height: "100vh" }}>
      {/* 全幅ヘッダー: 左にプロダクト名、右にアカウント */}
      <Flex
        asChild
        align="center"
        justify="between"
        gap="4"
        px="4"
        style={{
          height: "56px",
          flexShrink: 0,
          borderBottom: "1px solid var(--gray-a4)",
          background: "var(--color-panel-solid)",
        }}
      >
        <header>
          <Flex align="center" gap="2" style={{ minWidth: 0 }}>
            <Heading size="4" weight="bold" style={{ whiteSpace: "nowrap" }}>
              {headingText}
            </Heading>
            {headingDescription && (
              <Text size="2" color="gray">
                {headingDescription}
              </Text>
            )}
          </Flex>
          <AccountMenu
            email={email}
            sendPasswordResetEmailActionPath={sendPasswordResetEmailActionPath}
            logoutActionPath={logoutActionPath}
          />
        </header>
      </Flex>

      <Flex style={{ flex: 1, minHeight: 0 }}>
        <Nav navItems={navItems} bottomPopoverItems={bottomPopoverItems} />
        <main className="w-full flex-1 bg-[var(--background-color-secondary)] overflow-auto pl-[var(--space-2)] md:pl-4 pr-4 md:pr-4 pt-4 pb-4">
          {children}
        </main>
      </Flex>
    </Flex>
  );
}

export function CenterContentLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Flex style={{ height: "100vh" }} direction="column">
      <header className="flex flex-row w-full h-[80px] bg-gray-300">
        <div style={{ height: "80px" }} className="ml-6">
          <img
            src={logo}
            height="64"
            alt="かかりつけフィットネス"
            style={{
              height: "64px",
              width: "100%",
              objectFit: "cover",
              marginTop: "8px",
            }}
          />
        </div>
      </header>
      <main className="h-screen w-full bg-[var(--background-color-secondary)] overflow-auto pl-[var(--space-2)] md:pl-4 pr-4 md:pr-4 pt-10 pb-4">
        <Container width="440px" align="center">
          {children}
        </Container>
      </main>
    </Flex>
  );
}
